import type { SellerPermission } from "@/lib/api/types";
import { permissionGroupOf } from "./rbac-labels";

// Access-tier model for the role editor. Each area's raw permissions form a
// ladder (View → write verbs → destructive), so we present one ordered tier
// per area instead of a grid of 31 checkboxes. Tiers are supersets, which makes
// incoherent roles (e.g. Delete without View) unreachable from the simple UI.

export type TierKey = "none" | "view" | "manage" | "full";

// Fixed display order of the areas.
const AREA_ORDER = [
  "STORE",
  "PRODUCT",
  "CATEGORY",
  "ORDER",
  "COUPON",
  "REVIEW",
  "SHIPPING",
  "PAYMENT",
  "MEMBER",
  "ROLE",
] as const;

const DESTRUCTIVE_RE = /(_DELETE|_REMOVE)$/;

export type AreaDef = {
  group: string;
  perms: SellerPermission[]; // all permissions in this area
  view: SellerPermission | null;
  writes: SellerPermission[]; // non-view, non-destructive
  destructive: SellerPermission[];
  tiers: TierKey[]; // tiers that actually exist for this area
};

export function buildAreas(all: SellerPermission[]): AreaDef[] {
  const byGroup = new Map<string, SellerPermission[]>();
  for (const permission of all) {
    const group = permissionGroupOf(permission);
    const bucket = byGroup.get(group);
    if (bucket) bucket.push(permission);
    else byGroup.set(group, [permission]);
  }

  const seen = new Set<string>();
  const ordered = [
    ...AREA_ORDER.filter((g) => byGroup.has(g)),
    ...[...byGroup.keys()].filter((g) => !AREA_ORDER.includes(g as never)),
  ];

  const areas: AreaDef[] = [];
  for (const group of ordered) {
    if (seen.has(group)) continue;
    seen.add(group);
    const perms = byGroup.get(group);
    if (!perms || perms.length === 0) continue;
    const view = perms.find((p) => p.endsWith("_VIEW")) ?? null;
    const destructive = perms.filter((p) => DESTRUCTIVE_RE.test(p));
    const writes = perms.filter((p) => p !== view && !DESTRUCTIVE_RE.test(p));
    const tiers: TierKey[] = ["none"];
    if (view) tiers.push("view");
    if (writes.length > 0) tiers.push("manage");
    if (destructive.length > 0) tiers.push("full");
    areas.push({ group, perms, view, writes, destructive, tiers });
  }
  return areas;
}

export function permsForTier(area: AreaDef, tier: TierKey): SellerPermission[] {
  switch (tier) {
    case "none":
      return [];
    case "view":
      return area.view ? [area.view] : [];
    case "manage":
      return [...(area.view ? [area.view] : []), ...area.writes];
    case "full":
      return area.perms;
  }
}

// Which tier a selection corresponds to — or "custom" if it doesn't match any
// rung (e.g. a hand-built combo from the customize expander).
export function resolveTier(
  area: AreaDef,
  selected: Set<SellerPermission>,
): TierKey | "custom" {
  const sel = area.perms.filter((p) => selected.has(p));
  if (sel.length === 0) return "none";
  const matches = (tier: TierKey): boolean => {
    const want = permsForTier(area, tier);
    return want.length === sel.length && want.every((p) => selected.has(p));
  };
  if (matches("view")) return "view";
  if (area.tiers.includes("manage") && matches("manage")) return "manage";
  if (area.tiers.includes("full") && matches("full")) return "full";
  return "custom";
}

// Returns a new selection set with `area` set to exactly `tier`'s permissions.
export function applyTier(
  selected: Set<SellerPermission>,
  area: AreaDef,
  tier: TierKey,
): Set<SellerPermission> {
  const next = new Set(selected);
  for (const p of area.perms) next.delete(p);
  for (const p of permsForTier(area, tier)) next.add(p);
  return next;
}
